import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  type InputHTMLAttributes,
  forwardRef,
} from 'react';
import { AlertCircle, AlertTriangle, ArrowLeft, CheckCircle2, Info, X, Check, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Example-style UI primitives — warm cinematic palette + studio editing
 * feel. Mirrors the design language of Example-template-editor-prompt.
 *
 * All primitives use the `.lbl`, `.inp`, `.card`, `.btn-x-*`, `.pill-*`,
 * `.toggle-row-*` utility classes from globals.css.
 */

// ─── Field ────────────────────────────────────────────────────────
interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  counter?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, hint, error, required, counter, className, children }: FieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label ? (
        <label className="lbl">
          {label}
          {required ? <span className="ml-1 text-rose-400">*</span> : null}
          {counter ? (
            <span className="ml-2 text-dim font-normal tracking-normal normal-case">
              {counter}
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {error ? (
        <div className="text-xs text-red-400 mt-1" role="alert">{error}</div>
      ) : hint ? (
        <div className="text-[11px] text-dim leading-snug">{hint}</div>
      ) : null}
    </div>
  );
}

// ─── Input ───────────────────────────────────────────────────────
interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  counter?: string;
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, counter, leading, trailing, className, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} counter={counter}>
      <div className="relative">
        {leading ? (
          <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-dim">
            {leading}
          </div>
        ) : null}
        <input
          ref={ref}
          required={required}
          className={cn("inp", leading && "pl-9", trailing && "pr-9", error && "inp-error", className)}
          {...rest}
        />
        {trailing}
      </div>
    </Field>
  );
});

// ─── Textarea ──────────────────────────────────────────────────
interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  counter?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, counter, className, rows = 3, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} counter={counter}>
      <textarea
        ref={ref}
        required={required}
        rows={rows}
        className={cn("inp resize-y", error && "inp-error", className)}
        {...rest}
      />
    </Field>
  );
});

// ─── Select ────────────────────────────────────────────────────
interface SelectProps extends Omit<HTMLAttributes<HTMLSelectElement>, 'value'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children?: ReactNode;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  /** Optional options API for easier programmatic construction. */
  options?: Array<{ value: string; label: ReactNode; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, children, options, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      <select
        ref={ref}
        required={required}
        className={cn("inp cursor-pointer", error && "inp-error", className)}
        {...rest}
      >
        {children}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
});

// ─── Toggle (iOS-style switch) ────────────────────────────────
export function Toggle({
  checked,
  onChange,
  ariaLabel,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn("toggle-row-switch cursor-pointer", checked && "is-on", className)}
    />
  );
}

// ─── ToggleRow (label + description + switch) ──────────────────
export function ToggleRow({
  title,
  description,
  desc,
  checked,
  onChange,
}: {
  title: string;
  description?: string;
  desc?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const text = description ?? desc;
  return (
    <div className="toggle-row">
      <div className="min-w-0 flex-1">
        <div className="toggle-row-title">{title}</div>
        {text ? (
          <div className="toggle-row-desc">{text}</div>
        ) : null}
      </div>
      <Toggle checked={checked} onChange={onChange} ariaLabel={title} />
    </div>
  );
}

// ─── Pill (small label tag) ────────────────────────────────────
type PillTone = 'rose' | 'mint' | 'gold' | 'cream' | 'muted' | 'pink' | 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'violet' | 'blue' | 'green' | 'rose' | 'amber';

const VALID_PILL_TONES = ['rose', 'mint', 'gold', 'cream', 'muted', 'pink', 'success', 'warning', 'error', 'info', 'neutral', 'violet', 'blue', 'green', 'amber'] as const;

export function Pill({
  children,
  tone,
  variant,
  className,
  onClick,
}: {
  children: ReactNode;
  tone?: PillTone | string;
  variant?: PillTone | string;
  className?: string;
  onClick?: () => void;
}) {
  // Accept either `tone` (preferred) or legacy `variant`.
  const raw = (tone ?? variant ?? 'muted') as string;
  const safeTone = (VALID_PILL_TONES as readonly string[]).includes(raw) ? raw : 'muted';
  const baseClass = cn("pill", `pill-${safeTone}`, className);
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cn(baseClass, 'cursor-pointer')}>
        {children}
      </button>
    );
  }
  return <span className={baseClass}>{children}</span>;
}

// ─── Tier chip ────────────────────────────────────────────────
export function TierChip({ tier }: { tier: 'FREE' | 'PRO' }) {
  return (
    <span className={cn("tier-chip", tier === 'PRO' ? 'tier-chip-pro' : 'tier-chip-free')}>
      {tier}
    </span>
  );
}

// ─── Badge (small label) ─────────────────────────────────────
export function Badge({
  children,
  tone = 'muted',
  className,
}: {
  children: ReactNode;
  tone?: 'rose' | 'mint' | 'gold' | 'cream' | 'muted';
  className?: string;
}) {
  return <Pill tone={tone} className={className}>{children}</Pill>;
}

// ─── Buttons ──────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive' | 'success' | 'tertiary' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
}

const variantClass: Record<ButtonVariant, string> = {
  primary: 'btn-x-primary',
  secondary: 'btn-x-secondary',
  ghost: 'btn-x-ghost',
  danger: 'btn-x-danger',
  destructive: 'btn-x-danger',
  success: 'btn-x-success',
  tertiary: 'btn-x-ghost',
  outline: 'btn-x-secondary',
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'btn-x-sm',
  md: '',
  lg: 'btn-x-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', iconOnly, className, children, type, ...rest },
  ref,
) {
  const safeVariant = (variant in variantClass ? variant : 'secondary') as ButtonVariant;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(
        "btn-x",
        variantClass[safeVariant],
        sizeClass[size],
        iconOnly && 'btn-x-icon',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

// ─── Empty state ─────────────────────────────────────────────
export function Empty({
  icon,
  title,
  body,
  message,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  message?: ReactNode;
  action?: ReactNode;
}) {
  const text = body ?? message;
  return (
    <div className="empty-x">
      {icon ? <div className="empty-x-icon">{icon}</div> : null}
      <div className="empty-x-title">{title}</div>
      {text ? <div className="empty-x-body">{text}</div> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

// ─── PageHeader ──────────────────────────────────────────────────
//
// Title + subtitle + optional breadcrumb / actions / stats — the
// canonical header for every admin page. Mirrors the visual rhythm
// of `/templates/{id}` so all admin pages share a single header shape.
interface PageHeaderProps {
  /** Large bold title — typically the page or entity name. */
  title: ReactNode;
  /** Subtitle / lead sentence rendered in zinc-500. */
  description?: ReactNode;
  /** Tiny eyebrow label rendered above the title (e.g. "WORKSPACE"). */
  eyebrow?: ReactNode;
  /** Right-aligned slot for primary actions. */
  actions?: ReactNode;
  /** Optional back-link rendered as a chevron-left pill above the title. */
  back?: { label?: string; href?: string; onClick?: () => void };
  /** Optional inline stat row rendered beneath the title. */
  stats?: ReactNode;
  className?: string;
}

export function PageHeader({
  title, description, eyebrow, actions, back, stats, className,
}: PageHeaderProps) {
  return (
    <header className={cn("space-y-3", className)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1">
          {back && (
            <BackLink {...back} />
          )}
          {eyebrow && (
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-merry-pink">
              {eyebrow}
            </p>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
            {title}
          </h1>
          {description && (
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
      {stats && (
        <div className="flex flex-wrap items-center gap-2">{stats}</div>
      )}
    </header>
  );
}

function BackLink({ label = 'Back', href, onClick }: { label?: string; href?: string; onClick?: () => void }) {
  const cls = "inline-flex items-center gap-1 text-xs font-medium text-zinc-500 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50";
  if (href) {
    return (
      <a href={href} className={cls}>
        <ArrowLeft className="h-3 w-3" />
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      <ArrowLeft className="h-3 w-3" />
      {label}
    </button>
  );
}

// ─── TabBar (sliding pink underline) ────────────────────────
interface TabBarProps<T extends string> {
  value: T;
  // Accept either `{value, label}` or `{id, label}` (legacy compat).
  options: Array<{ value?: T; id?: T; label: ReactNode }>;
  onChange: (value: T) => void;
  className?: string;
}

export function TabBar<T extends string>({
  value,
  options,
  onChange,
  className,
}: TabBarProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState({ x: 0, w: 0 });

  useEffect(() => {
    const measure = () => {
      const node = refs.current[value];
      const container = containerRef.current;
      if (!node || !container) return;
      const nodeRect = node.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setIndicator({ x: nodeRect.left - containerRect.left, w: nodeRect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [value, options.length]);

  return (
    <nav ref={containerRef} className={cn("tabbar", className)} role="tablist">
      <span
        className="tabbar-indicator"
        style={{ transform: `translateX(${indicator.x}px)`, width: `${indicator.w}px` }}
        aria-hidden
      />
      {options.map((opt) => {
        const optValue = (opt.value ?? opt.id) as T;
        return (
          <button
            key={String(optValue)}
            ref={(el) => { refs.current[optValue] = el; }}
            type="button"
            role="tab"
            aria-selected={value === optValue}
            onClick={() => onChange(optValue)}
            className={cn("tabbar-item cursor-pointer", value === optValue && "is-active")}
          >
            {opt.label}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Toaster ─────────────────────────────────────────────────
type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  durationMs: number;
}

interface ToastContextValue {
  showToast: (opts: {
    title: string;
    description?: string;
    variant?: ToastVariant;
    durationMs?: number;
  }) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <Toaster>');
  return ctx;
}

export function Toaster({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback<ToastContextValue['showToast']>(
    ({ title, description, variant = 'info', durationMs = 4000 }) => {
      const id = `toast-${Math.random().toString(36).slice(2, 9)}`;
      setToasts((curr) => [...curr, { id, title, description, variant, durationMs }]);
      return id;
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    const t = window.setTimeout(() => onDismiss(toast.id), toast.durationMs);
    return () => clearTimeout(t);
  }, [toast.id, toast.durationMs, onDismiss]);

  const Icon = ICONS[toast.variant];

  return (
    <div role="status" className={cn("toast-x", `toast-x-${toast.variant}`)}>
      <div className="toast-x-icon">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="toast-x-title">{toast.title}</div>
        {toast.description ? (
          <div className="toast-x-desc">{toast.description}</div>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 rounded-md p-1 text-dim transition hover:bg-white/5 hover:text-cream cursor-pointer"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Range slider ────────────────────────────────────────────
export function Range({
  min = 0,
  max = 1,
  step = 0.05,
  value,
  onChange,
  className,
  label,
}: {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
  className?: string;
  label?: string;
}) {
  return (
    <div className={cn("w-full", className)}>
      {label ? <div className="lbl">{label}</div> : null}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="slider"
      />
    </div>
  );
}

// ─── ProposalPills (multi-select proposal type chips) ───────
export function ProposalPills({
  value,
  onChange,
  options,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: Array<{ value: string; label: string; hint?: string }>;
}) {
  function toggle(t: string) {
    if (value.includes(t)) {
      if (value.length === 1) return;
      onChange(value.filter((v) => v !== t));
    } else {
      onChange([...value, t]);
    }
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            title={opt.hint}
            className={cn(
              "cursor-pointer rounded-full border px-3.5 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.14em] transition-all duration-200",
              active
                ? "border-rose/60 bg-rose/15 text-blush shadow-[0_0_16px_rgba(244,88,126,.25)]"
                : "border-line bg-panel/50 text-muted hover:border-soft hover:text-cream",
            )}
          >
            {active ? <Check size={11} className="mr-1 inline -translate-y-px" /> : null}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}